import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, ArrowRight, Zap, ShieldCheck, Award, TrendingUp, Star, Quote, Send, CheckCircle2, AlertTriangle, ChevronDown, ChevronUp, Check, ChevronLeft, ChevronRight, X, Maximize2 } from 'lucide-react';
import { companyInfo } from '../data';
import ScrollReveal from '../components/ScrollReveal';
import useMeta from '../hooks/useMeta';

// Map icon names to Lucide components
const iconMap = {
  Zap: Zap,
  ShieldCheck: ShieldCheck,
  Award: Award,
  TrendingUp: TrendingUp,
};

export default function Home() {
  useMeta(
    'LJR Teknik ApS | Professionel Service af Gaffeltruck & Maskiner',
    'LJR Teknik ApS tilbyder professionel service, reparation og lovpligtigt eftersyn af gaffeltrucks og mindre maskiner. Hurtig udrykning og over 15 års erfaring.'
  );

  const [services, setServices] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formStatus, setFormStatus] = useState({ type: null, message: '' });
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [expandedFeatures, setExpandedFeatures] = useState(false);
  const [testimonialIndex, setTestimonialIndex] = useState(0);

  const nextTestimonial = () => {
    if (testimonials.length === 0) return;
    setTestimonialIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    if (testimonials.length === 0) return;
    setTestimonialIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const getVisibleTestimonials = () => {
    if (testimonials.length === 0) return [];
    const items = [];
    for (let i = 0; i < 3; i++) {
      items.push(testimonials[(testimonialIndex + i) % testimonials.length]);
    }
    return items;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormStatus({ type: null, message: '' });

    const form = e.currentTarget;
    const formData = {
      name: form.elements.hero_name.value,
      phone: form.elements.hero_phone.value,
      email: form.elements.hero_email.value,
      message: form.elements.hero_message.value,
      website_url: form.elements.website_url.value, // Honeypot
    };

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok && result.status === 'ok') {
        setFormStatus({
          type: 'success',
          message: 'Tak! Jeg har modtaget din besked og kontakter dig hurtigst muligt.'
        });
        form.reset();
      } else {
        setFormStatus({
          type: 'error',
          message: result.message || 'Der opstod en fejl. Prøv venligst igen.'
        });
      }
    } catch (error) {
      console.error('Fejl ved afsendelse:', error);
      setFormStatus({
        type: 'error',
        message: 'Netværksfejl. Kontroller din forbindelse og prøv igen.'
      });
    } finally {
      setFormSubmitting(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, testimonialsRes] = await Promise.all([
          fetch('https://api.dev.auxo.dk/public/content/services'),
          fetch('https://api.dev.auxo.dk/public/content/testimonials')
        ]);

        if (servicesRes.ok) {
          const servicesData = await servicesRes.json();
          setServices(servicesData);
        }
        if (testimonialsRes.ok) {
          const testimonialsData = await testimonialsRes.json();
          setTestimonials(testimonialsData);
        }
      } catch (error) {
        console.error('Fejl ved hentning af CMS data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <div className="bg-zinc-50 text-zinc-900">
      {/* Hero Section */}
      <section className="relative min-h-[55vh] flex items-center bg-zinc-950 text-white overflow-hidden border-b border-zinc-850">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <img
            src="https://assets.auxodev.dk/assets/ac914a03-55b8-406d-88cf-a09072b72053/image00001.png"
            alt="Gaffeltruck Service"
            className="h-full w-full object-cover object-center animate-slow-zoom"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/85 to-zinc-950/20" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 py-12 md:py-16 lg:px-8 w-full">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Left Column: Text & Buttons */}
            <div className="md:col-span-6 lg:col-span-7 space-y-5">
              <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl md:text-5xl uppercase text-white">
                Professionel Service på <span className="text-white">Gaffeltrucks</span> & Mindre Maskiner
              </h1>
              <p className="text-base text-white leading-relaxed">
                Jeg tilbyder hurtig fejlfinding, reparation og lovpligtige sikkerhedseftersyn. Min fuldt udstyrede servicevogn rykker ud til din adresse, så du undgår dyre driftsstop.
              </p>
              <div className="flex flex-wrap gap-4 pt-2">
                <a
                  href={`tel:${companyInfo.phone}`}
                  className="inline-flex items-center gap-2 rounded-full bg-red-600 px-6 py-3.5 text-sm font-bold text-white hover:bg-red-700 shadow-lg shadow-red-600/20 transition-all"
                >
                  <Phone className="h-4 w-4" />
                  <span>{companyInfo.phone}</span>
                </a>
              </div>
            </div>

            {/* Right Column: Webform */}
            <div className="md:col-span-6 lg:col-span-5 w-full">
              <div className="relative overflow-hidden bg-zinc-950 border border-zinc-800 p-6 md:p-8 rounded-3xl shadow-xl text-white">
                {/* Red semicircle watermark on the right side */}
                <div className="absolute right-0 top-1/2 -translate-y-1/2 w-48 h-96 bg-red-600/80 rounded-l-full pointer-events-none z-0" />

                <div className="relative z-10 space-y-4">
                  <div className="space-y-1 text-center px-4">
                    <h3 className="text-lg font-semibold text-white uppercase tracking-wide">Få et uforpligtende tilbud</h3>
                    <p className="text-xs text-white">Udfyld formularen, så kontakter jeg dig hurtigst muligt.</p>
                  </div>

                  {formStatus.type === 'success' && (
                    <div className="flex items-start gap-2 rounded-lg bg-emerald-950/50 border border-emerald-800 p-3 text-emerald-200">
                      <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" />
                      <p className="text-xs font-medium">{formStatus.message}</p>
                    </div>
                  )}

                  {formStatus.type === 'error' && (
                    <div className="flex items-start gap-2 rounded-lg bg-rose-950/50 border border-rose-800 p-3 text-rose-200">
                      <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                      <p className="text-xs font-medium">{formStatus.message}</p>
                    </div>
                  )}

                  <form onSubmit={handleFormSubmit} className="space-y-3">
                    {/* Honeypot field - ABSOLUTELY HIDDEN from humans */}
                    <div className="sr-only absolute -top-96 -left-96" aria-hidden="true">
                      <label htmlFor="website_url">Do not fill this out if you are human</label>
                      <input
                        type="text"
                        name="website_url"
                        id="website_url"
                        tabIndex="-1"
                        autoComplete="off"
                      />
                    </div>

                    <div className="space-y-1">
                      <label htmlFor="hero_name" className="text-[10px] font-bold text-zinc-300 uppercase tracking-wider">
                        Navn <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="hero_name"
                        id="hero_name"
                        required
                        placeholder="Dit navn"
                        className="w-full border-b border-white rounded-none bg-transparent px-1 py-2 text-xs text-white placeholder-zinc-400 focus:border-red-500 focus:ring-0 focus:outline-none"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <label htmlFor="hero_phone" className="text-[10px] font-bold text-zinc-300 uppercase tracking-wider">
                          Telefon <span className="text-rose-500">*</span>
                        </label>
                        <input
                          type="tel"
                          name="hero_phone"
                          id="hero_phone"
                          required
                          placeholder="Tlf. nr."
                          className="w-full border-b border-white rounded-none bg-transparent px-1 py-2 text-xs text-white placeholder-zinc-400 focus:border-red-500 focus:ring-0 focus:outline-none"
                        />
                      </div>
                      <div className="space-y-1">
                        <label htmlFor="hero_email" className="text-[10px] font-bold text-zinc-300 uppercase tracking-wider">
                          E-mail <span className="text-rose-500">*</span>
                        </label>
                        <input
                          type="email"
                          name="hero_email"
                          id="hero_email"
                          required
                          placeholder="E-mail"
                          className="w-full border-b border-white rounded-none bg-transparent px-1 py-2 text-xs text-white placeholder-zinc-400 focus:border-red-500 focus:ring-0 focus:outline-none"
                        />
                      </div>
                    </div>

                    <div className="space-y-1">
                      <label htmlFor="hero_message" className="text-[10px] font-bold text-zinc-300 uppercase tracking-wider">
                        Besked <span className="text-rose-500">*</span>
                      </label>
                      <textarea
                        name="hero_message"
                        id="hero_message"
                        required
                        rows="3"
                        placeholder="Hvad kan jeg hjælpe med?"
                        className="w-full border-b border-white rounded-none bg-transparent px-1 py-2 text-xs text-white placeholder-zinc-400 focus:border-red-500 focus:ring-0 focus:outline-none resize-none"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={formSubmitting}
                      className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-red-600 py-2.5 text-xs font-bold text-white hover:bg-red-700 disabled:bg-zinc-100 disabled:text-zinc-400 disabled:cursor-not-allowed transition-all shadow-md shadow-red-600/10 cursor-pointer"
                    >
                      {formSubmitting ? (
                        <>
                          <div className="h-3 w-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          <span>Sender...</span>
                        </>
                      ) : (
                        <>
                          <Send className="h-3 w-3" />
                          <span>Send forespørgsel</span>
                        </>
                      )}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative py-16 md:py-24 bg-white border-b border-zinc-200 overflow-hidden">
        {/* Left half semicircle background effect */}
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[380px] h-[760px] bg-zinc-100 rounded-r-full hidden lg:block z-0" />

        <div className="relative z-10 mx-auto max-w-7xl px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Left Column: Stacked Boxes with Expand Button */}
            <div className="lg:col-span-7 space-y-4 order-2 lg:order-1">
              <ScrollReveal variant="fade-left">
                <div className="space-y-4 transition-all duration-500">
                  {companyInfo.features
                    .slice(0, expandedFeatures ? companyInfo.features.length : 2)
                    .map((feature, index) => {
                      const IconComponent = iconMap[feature.icon] || ShieldCheck;
                      const isFirst = index === 0;
                      return (
                        <div
                          key={index}
                          className={`relative flex flex-col sm:flex-row gap-4 p-6 rounded-xl border transition-all duration-300 ${
                            isFirst
                              ? 'bg-zinc-950 text-white border-zinc-850 shadow-lg shadow-zinc-950/10'
                              : 'bg-white text-zinc-900 border-zinc-200 shadow-md hover:shadow-lg'
                          }`}
                        >
                          <div className={`shrink-0 pt-1 ${isFirst ? 'text-white' : 'text-zinc-900'}`}>
                            <IconComponent className="h-6 w-6" />
                          </div>
                          <div className="space-y-1">
                            <h4 className={`text-lg font-semibold uppercase ${isFirst ? 'text-white' : 'text-zinc-900'}`}>{feature.title}</h4>
                            <p className={`text-sm leading-relaxed ${isFirst ? 'text-zinc-300' : 'text-zinc-600'}`}>{feature.description}</p>
                          </div>
                        </div>
                      );
                    })}
                </div>

                {/* Expand Button */}
                <div className="pt-2 flex justify-start">
                  <button
                    onClick={() => setExpandedFeatures(!expandedFeatures)}
                    className="inline-flex items-center gap-2 text-xs font-bold text-zinc-800 hover:text-zinc-950 transition-all cursor-pointer py-2"
                  >
                    <span>{expandedFeatures ? 'Vis færre fordele' : 'Udvid til mere'}</span>
                    {expandedFeatures ? (
                      <ChevronUp className="h-4 w-4 text-zinc-900" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-zinc-900" />
                    )}
                  </button>
                </div>
              </ScrollReveal>
            </div>

            {/* Right Column: Text Content */}
            <div className="lg:col-span-5 space-y-6 lg:sticky lg:top-24 order-1 lg:order-2">
              <ScrollReveal variant="fade-right">
                <div className="space-y-3">
                  <h3 className="text-3xl font-semibold tracking-tight text-zinc-900 sm:text-4xl uppercase leading-tight">
                    Jeg holder dine maskiner kørende og din forretning i gang
                  </h3>
                </div>
                <p className="text-zinc-600 leading-relaxed mt-4">
                  Med fokus på kvalitet, hurtighed og gennemskuelighed leverer jeg service, du kan stole på hver gang. Jeg forstår, at nedetid koster dyrt, og derfor arbejder jeg målrettet på at minimere dine driftsforstyrrelser.
                </p>
                <div className="pt-4 mt-6 border-t border-zinc-100">
                  <p className="text-sm font-semibold text-zinc-900 uppercase mb-2">Mit løfte til dig:</p>
                  <ul className="space-y-2 text-sm text-zinc-600">
                    <li className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-zinc-900 shrink-0" />
                      Hurtig respons på alle henvendelser
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-zinc-900 shrink-0" />
                      Gennemskuelige priser uden skjulte gebyrer
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-zinc-900 shrink-0" />
                      Kvalificerede teknikere med rette udstyr
                    </li>
                  </ul>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview Section */}
      <section className="py-16 md:py-24 bg-zinc-50">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <ScrollReveal variant="fade-up">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
              <div className="space-y-3 max-w-2xl">
                <p className="text-3xl font-semibold tracking-tight text-zinc-900 sm:text-4xl uppercase">
                  Professionel service til alle behov
                </p>
                <p className="text-zinc-600">
                  Jeg dækker alt fra akutte reparationer af gaffeltrucks til forebyggende vedligeholdelse af mindre maskiner.
                </p>
              </div>
              <Link
                to="/service"
                className="inline-flex items-center gap-1.5 text-sm font-bold text-zinc-900 hover:text-black transition-colors shrink-0"
              >
                <span>Se alle ydelser</span>
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </ScrollReveal>

          {loading ? (
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3].map((n) => (
                <div key={n} className="animate-pulse bg-white rounded-xl border border-zinc-200 h-96" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
              {services.map((service, idx) => {
                const title = service.title?.da || service.title || '';
                const desc = service.description?.da || service.description || '';
                const slug = service.slug?.da || service.slug || service.id;
                const imgUrl = service.image?.[0] || '';

                return (
                  <ScrollReveal key={service.id} variant="fade-up" delay={idx * 100} className="flex flex-col h-full">
                    <div className="flex flex-col bg-white rounded-xl border border-zinc-200 overflow-hidden hover:shadow-lg transition-all group h-full">
                      <div className="relative h-48 overflow-hidden bg-zinc-100">
                        <img
                          src={imgUrl}
                          alt={title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <div className="p-6 flex flex-col flex-grow">
                        <h3 className="text-xl font-semibold text-zinc-900 mb-2 group-hover:text-black transition-colors uppercase">
                          {title}
                        </h3>
                        <p className="text-sm text-zinc-600 leading-relaxed mb-6 flex-grow">
                          {desc}
                        </p>
                        <Link
                          to={`/service/${slug}`}
                          className="inline-flex items-center gap-1 text-sm font-bold text-zinc-900 hover:text-black transition-colors mt-auto"
                        >
                          <span>Læs mere</span>
                          <ArrowRight className="h-4 w-4" />
                        </Link>
                      </div>
                    </div>
                  </ScrollReveal>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Emergency CTA Banner */}
      <section className="bg-zinc-950 text-white py-16 relative overflow-hidden border-y border-zinc-800">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/ac914a03-55b8-406d-88cf-a09072b72053/image00010_2.jpeg"
            alt="Værksted"
            className="h-full w-full object-cover object-center opacity-35"
          />
          <div className="absolute inset-0 bg-zinc-950/70" />
        </div>
        <div className="relative z-10 mx-auto max-w-5xl px-4 text-center">
          <ScrollReveal variant="zoom-in">
            <div className="space-y-6">
              <div className="inline-flex items-center justify-center h-14 w-14 text-white mb-2">
                <Zap className="h-7 w-7 animate-bounce" />
              </div>
              <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl uppercase">
                Brug for hurtig hjælp til din maskine?
              </h2>
              <p className="text-zinc-300 max-w-2xl mx-auto text-base">
                Jeg ved, at hvert minut tæller. Ring til mig med det samme. Jeg tilbyder hurtig udrykning og klarer de fleste reparationer direkte på stedet.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
                <a
                  href={`tel:${companyInfo.phone}`}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-red-600 px-8 py-4 text-base font-bold text-white hover:bg-red-700 shadow-lg shadow-red-600/20 transition-all"
                >
                  <Phone className="h-5 w-5" />
                  <span>Ring direkte: {companyInfo.phone}</span>
                </a>
                <Link
                  to="/kontakt"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full border border-white bg-zinc-900 px-8 py-4 text-base font-semibold text-white hover:bg-zinc-800 transition-all"
                >
                  <span>Book service online</span>
                </Link>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
